import React from 'react';
import { Badge } from '../ui';
import { formatDate, formatPercent } from '../portal/format';
import portal from '../portal/Portal.module.css';
import styles from './Reports.module.css';

export const EXAM_TYPE_LABELS = { continuous: 'Continuous', mid_term: 'Mid Term', end_term: 'End of Term', zimsec_mock: 'Mock' };
export const STATUS = {
  draft: { label: 'Draft', badge: 'default' },
  submitted: { label: 'Submitted', badge: 'warning' },
  published: { label: 'Published', badge: 'success' },
};
export const examTypeLabel = (type) => EXAM_TYPE_LABELS[type] || (type || '').replace(/_/g, ' ');
const mark = (value) => (value === null || value === undefined ? '—' : `${value}`);

// A term report as it reads on paper. `report` is the payload of
// GET /reports/cards/<id> — the same data the PDF is built from.
function ReportView({ report, schoolName }) {
  const aLevel = report.level === 'a_level';
  const attendance = report.attendance;

  return (
    <div className={styles.sheet}>
      <div className={styles.sheetHead}>
        <div>
          <div className={styles.sheetName}>{report.student.full_name}</div>
          <div className={styles.sheetMeta}>{[schoolName, report.student.class_name, report.term.label, report.level_name].filter(Boolean).join(' · ')}</div>
        </div>
        <Badge variant={STATUS[report.status]?.badge || 'default'}>{STATUS[report.status]?.label || report.status}</Badge>
      </div>

      <div className={styles.sheetBody}>
        <h3 className={styles.sectionTitle}>Academic performance</h3>
        {report.subjects.length === 0 ? (
          <p className={styles.empty}>No marks have been recorded for this term.</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.marks}>
              <thead>
                <tr>
                  <th>Subject</th>
                  {report.exam_types.map((type) => <th key={type} className={styles.num}>{examTypeLabel(type)}</th>)}
                  <th className={styles.num}>Final</th>
                  <th>Grade</th>
                  {aLevel && <th className={styles.num}>Points</th>}
                  <th>Teacher's remark</th>
                </tr>
              </thead>
              <tbody>
                {report.subjects.map((subject) => {
                  const scores = Object.fromEntries(subject.assessments.map((item) => [item.exam_type, item.score]));
                  return (
                    <tr key={subject.subject_id}>
                      <td><strong>{subject.subject}</strong></td>
                      {report.exam_types.map((type) => <td key={type} className={styles.num}>{mark(scores[type])}</td>)}
                      <td className={styles.final}>{mark(subject.final_mark)}</td>
                      <td><span className={styles.grade}>{subject.grade || '—'}</span>{subject.descriptor && <span className={styles.descriptor}>{subject.descriptor}</span>}</td>
                      {aLevel && <td className={styles.num}>{mark(subject.points)}</td>}
                      <td>{subject.remark || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className={portal.stats} style={{ marginTop: '1rem' }}>
          <div className={portal.stat}><div className={portal.statLabel}>Overall average</div><div className={portal.statValue}>{report.average != null ? `${report.average}%` : '—'}</div></div>
          <div className={portal.stat}><div className={portal.statLabel}>Position in class</div><div className={portal.statValue}>{report.position ? `${report.position} of ${report.class_size}` : '—'}</div></div>
          <div className={portal.stat}><div className={portal.statLabel}>Class average</div><div className={portal.statValue}>{report.class_average != null ? `${report.class_average}%` : '—'}</div></div>
          {aLevel ? (
            <div className={portal.stat}><div className={portal.statLabel}>Total points</div><div className={portal.statValue}>{mark(report.points_total)}</div></div>
          ) : (
            <div className={portal.stat}><div className={portal.statLabel}>Subjects passed (C or better)</div><div className={portal.statValue}>{report.passed_count} of {report.subjects_count}</div></div>
          )}
        </div>

        {attendance && (
          <>
            <h3 className={styles.sectionTitle}>Attendance</h3>
            <div className={portal.stats}>
              <div className={portal.stat}><div className={portal.statLabel}>Attendance rate</div><div className={portal.statValue}>{formatPercent(attendance.rate)}</div></div>
              {['present', 'late', 'absent', 'excused'].map((key) => (
                <div key={key} className={portal.stat}><div className={portal.statLabel}>{key.charAt(0).toUpperCase() + key.slice(1)}</div><div className={portal.statValue}>{attendance[key]}</div></div>
              ))}
            </div>
          </>
        )}

        <h3 className={styles.sectionTitle}>Comments</h3>
        <div className={styles.comment}>
          <div className={styles.commentLabel}>Class teacher's comment</div>
          <div className={styles.commentText}>{report.class_teacher_comment || '—'}</div>
          {report.class_teacher_name && <div className={styles.commentAuthor}>{report.class_teacher_name}</div>}
        </div>
        <div className={styles.comment}>
          <div className={styles.commentLabel}>Head's comment</div>
          <div className={styles.commentText}>{report.head_comment || '—'}</div>
        </div>

        <p className={styles.legend}>
          <strong>{report.level_name} grading:</strong>{' '}
          {report.scale.map((row) => `${row.grade} ${row.range} ${row.descriptor}${row.points != null ? ` (${row.points} pts)` : ''}`).join(' · ')}
          {report.published_at && ` · Published ${formatDate(report.published_at)}`}
        </p>
      </div>
    </div>
  );
}

export default ReportView;
